import React, { CSSProperties, FC, memo, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { EThemeScheme } from '@webtrader/core/themes';

interface IProps {
  size?: number;
  color?: string;
  loading?: boolean;
}

interface ISpinnerProps {
  size?: number;
  borderWidth?: number;
  forDarkBackground?: boolean;
}

const Spinner = styled.div<Partial<ISpinnerProps>>`
  @keyframes spinner {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  border: ${({ borderWidth }) => `${borderWidth}px`} solid;

  ${({ forDarkBackground }) => {
    if (forDarkBackground) {
      return css`
        border-color: rgba(255, 255, 255, 0);
        border-right-color: rgba(255, 255, 255, 1);
      `;
    }

    return css`
      border-color: rgba(150, 160, 170, 0.4);
      border-right-color: rgba(150, 160, 170, 0.9);
    `;
  }};
  border-radius: 60%;

  animation: spinner 1.2s linear infinite;
`;

const LoadingSpinner: FC<IProps> = (props) => {
  const { color, size = 20 } = props;

  const { theme } = useTheme();

  const forDarkBackground = useMemo(() => {
    const { scheme } = theme;

    switch (scheme) {
      case EThemeScheme.DARK:
        return true;
      case EThemeScheme.LIGHT:
        return false;
      default:
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  }, [theme]);

  const spinColor = useMemo(() => {
    return color || theme.colors.text[0];
  }, [color, theme]);

  const adjustedSize = useMemo(() => size / 1.25, [size]);

  const styles = useMemo(() => {
    const spin: CSSProperties = {
      width: adjustedSize,
      height: adjustedSize
    };

    return spin;
  }, [adjustedSize]);

  return (
    <Spinner
      size={dynamicSize.convertSize(adjustedSize)}
      borderWidth={2}
      color={spinColor}
      forDarkBackground={forDarkBackground}
      style={styles}
      data-testid={EDataTestId.SPIN_LOADER}
    />
  );
};

export default memo(LoadingSpinner);
